import _jsx from "@babel/runtime/helpers/jsx";
import React, { useEffect, useState, useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { deprecate } from 'react-is-deprecated';
import Box from '../Box/index.js';
import Button from '../Button/index.js';

/**
 * Desktop-specific implementation of `PopoverSheet` that displays content in an elevated container with a CTA button
 * that has a callback handler.
 *
 * DesktopPopover renders as a floating container anchored relative to a trigger element. If a containerElement is given
 * it will fill the width of the container.
 */

const DesktopPopover = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    show,
    children,
    buttonLabel,
    className,
    containerElement,
    hideCTA,
    fullWidth = true,
    position = 'left',
    onClick,
    // deprecated
    onHide = () => {},
    renderServerSide = false,
    ...rest
  } = _ref;
  const intialShow = renderServerSide || show;
  const [showPopover, setShowPopover] = useState(intialShow); // to show the popover component
  const [openAnimation, setOpenAnimation] = useState(intialShow); // to set the animation
  const [renderedInDom, setRenderedInDom] = useState(renderServerSide); // to render content serverside -- for SEO

  useEffect(() => {
    if (show) {
      setOpenAnimation(true);
      if (renderServerSide) setRenderedInDom(false);
      if (!renderServerSide) setShowPopover(true);
    } else if (!show && !renderedInDom) {
      setOpenAnimation(false);
      if (!renderServerSide) {
        setTimeout(() => {
          setShowPopover(false);
        }, 250); // Need time to show animation before the component is hidden
      }
    }
  }, [show, renderedInDom, renderServerSide]);
  const handleHideClick = useCallback(() => {
    // handle deprecated onClick callback for onHide functionaliuty
    if (typeof onClick === 'function') {
      onClick();
    }
    onHide();
  }, [onClick, onHide]);
  if (!showPopover) {
    return null;
  }
  const popOverWidth = fullWidth ? '100%' : 'initial';
  const style = {
    zIndex: 3,
    // position attaches the component to that edge of the parent element.
    [position]: '0px',
    width: containerElement ? containerElement.offsetWidth + 'px' : popOverWidth
  };
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    ref: ref,
    "data-cmp": "popoverSheet",
    className: clsx('overlay-shadow margin-top-2 position-absolute', {
      'fade-in-slide-down': openAnimation,
      'fade-out-slide-up': !openAnimation,
      'display-none': renderedInDom
    }, className),
    backgroundColor: "neutral-primary",
    borderColor: "transparent",
    style: style
  }, rest), children, !hideCTA && /*#__PURE__*/_jsx(Button, {
    block: true,
    bsStyle: "primary",
    onClick: handleHideClick,
    className: "margin-top-5"
  }, void 0, buttonLabel));
});
export default DesktopPopover;